import { useState, useEffect } from 'react';
import { Button, Form, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import DoctorImg from '../assets/images/doctor.png';
import PatientImg from '../assets/images/patient.png';

export default function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    userType: '',
    fname: '',
    lname: '',
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (Cookies.get('userToken')) {
      const userData = JSON.parse(Cookies.get('userData'));
      navigate(userData.role === 'admin' ? '/doctor-dashboard' : '/patient-dashboard');
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.userType) newErrors.userType = 'Please select a user type';
    if (!formData.fname.trim()) newErrors.fname = 'First name is required';
    if (!formData.lname.trim()) newErrors.lname = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Replace with your actual API call
      const response = await fetch('your-api-endpoint', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      
      Cookies.set('userToken', data.token, { secure: true });
      Cookies.set('userData', JSON.stringify({
        role: formData.userType,
        name: `${formData.fname} ${formData.lname}`
      }), { secure: true });

      // Redirect based on user type
      navigate(formData.userType === 'doctor' ? '/doctor-dashboard' : '/patient-dashboard');

    } catch (err) {
      setApiError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-wrap-left pt-4">
        <h1>Welcome to Doctor Assistant</h1>
      </div>
      <div className="login-wrap-right d-flex align-items-center py-5 px-3 flex-column">
        <h1>Create your account</h1>
        <div className="form-wrapper">
          {apiError && <Alert variant="danger">{apiError}</Alert>}
          
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <Form.Label>Choose User Type</Form.Label>
              <div>
                <label className="custom-radio-btn">
                  <input
                    type="radio"
                    name="userType"
                    value="doctor"
                    checked={formData.userType === 'doctor'}
                    onChange={handleChange}
                  />
                  <span className="checkMark">
                    <img src={DoctorImg} alt="Doctor" />
                    Doctor
                  </span>
                </label>
                <label className="custom-radio-btn ms-3">
                  <input
                    type="radio"
                    name="userType"
                    value="patient"
                    checked={formData.userType === 'patient'}
                    onChange={handleChange}
                  />
                  <span className="checkMark">
                    <img src={PatientImg} alt="Patient" />
                    Patient
                  </span>
                </label>
              </div>
              {errors.userType && <div className="text-danger small">{errors.userType}</div>}
            </div>

            <div className="mb-3">
              <Form.Label htmlFor="fname">First Name</Form.Label>
              <Form.Control
                type="text"
                id="fname"
                name="fname"
                value={formData.fname}
                onChange={handleChange}
                isInvalid={!!errors.fname}
              />
              <Form.Control.Feedback type="invalid">
                {errors.fname}
              </Form.Control.Feedback>
            </div>

            <div className="mb-3">
              <Form.Label htmlFor="lname">Last Name</Form.Label>
              <Form.Control
                type="text"
                id="lname"
                name="lname"
                value={formData.lname}
                onChange={handleChange}
                isInvalid={!!errors.lname}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lname}
              </Form.Control.Feedback>
            </div>

            <div className="mb-3">
              <Form.Label htmlFor="email">Email</Form.Label>
              <Form.Control
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </div>

            <div className="mb-3">
              <Form.Label htmlFor="password">Password</Form.Label>
              <Form.Control
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                isInvalid={!!errors.password}
              />
              <Form.Control.Feedback type="invalid">
                {errors.password}
              </Form.Control.Feedback>
              <Form.Text muted>
                Must be at least 8 characters long
              </Form.Text>
            </div>

            <div className="text-center mt-5 mb-5">
              <Button
                variant="success"
                className="login-btn"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Registering...' : 'Register'}
              </Button>
            </div>
          </form>

          <p className="text-center small">
            Already have an account? <Link to="/login">Login</Link> here
          </p>
        </div>
      </div>
    </div>
  );
}